import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  EmailIcon,
  ExclamationMarkOutlinedIcon,
  FamilyIcon,
  MessageIcon,
  PlusIcon,
  UploadIcon,
  UserCanceledIcon,
  UserCheckIcon
} from '@repo/ui/icons'
import { LogSection } from '@repo/ui/log-section'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import RoundedIcon from '@repo/ui/rounded-icon'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { sendEmailToHostFamilies } from '../../../api/host-families/send-email-to-host-family'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import SendEmailForm from '../../../components/pages/candidates/send-email-form'
import { HostFamilyFilter } from '../../../components/pages/host-families/host-family-filter'
import { Paths } from '../../../lib/constants/path'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { HostFamily, HostFamilyFilters } from '../../../lib/types/models/host-family/host-family'
import { HostFamilyProperties } from '../../../lib/types/models/host-family/properties.host-family'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformHostFamilyFiltersToUrl,
  transformUrlToHostFamilyFilters
} from '../../../lib/utils/filters-mapping/host-family'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'

const hostFamilyFilter: HostFamilyFilters = {
  deactivated: false
}

const OlPersonalSPaceHostFamiliesListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = useRef<DynamicTableRef<HostFamily>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const {
    toggleModal: toggleLecComments,
    modalState: lecCommentsModalState,
    closeModel: closeLecCommentsModal,
    selectedData: selectedLecComments
  } = useModalManager<'preview', HostFamily['lecComments']>(['preview'])

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'send-email' | 'filters',
    HostFamily[]
  >(['send-email', 'filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<HostFamilyFilters>({
      initialFilters: hostFamilyFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformHostFamilyFiltersToUrl,
      transformUrlToFilter: transformUrlToHostFamilyFilters
    })

  const { mutate: sendEmailToHostFamiliesMutation, isPending } = useMutation({
    mutationFn: sendEmailToHostFamilies, // make the actual call
    onSuccess: () => {
      toast.success('Emails sent successfully')
      closeModel('send-email')
    },
    onError: showAxiosError
  })

  const columns: Column<HostFamily>[] = [
    {
      accessorKey: 'deactivated',
      header: '',
      cell: ({ row }) => (
        <div className={'w-12 flex justify-center'}>
          {row.original.deactivated ? (
            <RoundedIcon variant={'danger'} icon={UserCanceledIcon} />
          ) : (
            <RoundedIcon variant={'success'} icon={UserCheckIcon} />
          )}
        </div>
      )
    },
    {
      accessorKey: 'familyName',
      header: 'Family name',
      enableSorting: true,
      cell: ({ row }) => (
        <span className={cn(row.original.deactivatedAsNotSuitable && 'text-red')}>
          {row.original.familyName}
        </span>
      )
    },
    {
      accessorKey: 'parent1FirstName',
      header: 'Firstname Mother',
      enableSorting: true,
      cell: ({ row }) => (
        <span className={cn(row.original.deactivatedAsNotSuitable && 'text-red')}>
          {row.original.parent1FirstName}
        </span>
      )
    },
    {
      accessorKey: 'parent1PhoneNumber',
      header: 'Number Mother',
      cell: ({ row }) =>
        row.original.parent1PhoneNumber ? formatPhoneNumber(row.original.parent1PhoneNumber) : '-'
    },
    {
      accessorKey: 'city',
      header: 'Town',
      enableSorting: true
    },
    {
      accessorKey: 'yearsWithLec',
      header: 'Seniority (years)',
      enableSorting: true
    },
    {
      accessorKey: 'studentsCount',
      header: '# of students hosted',
      enableSorting: true,
      cell: ({ row }) => <div className={'text-center'}>{row.original.studentsCount}</div>
    },
    //todo: add welcome
    {
      accessorKey: 'petDescription',
      header: 'Pets',
      enableSorting: true,
      cell: ({ row }) => <div className={'text-center'}>{row.original.petDescription}</div>
    },
    //todo: add meals
    {
      accessorKey: 'lecComments',
      header: 'LEC comments',
      cell: ({ row }) =>
        row.original.lecComments.length > 0 ? (
          <ActionButton
            icon={ExclamationMarkOutlinedIcon}
            variant={'lightPurple'}
            onClick={(e) => {
              e.stopPropagation()
              toggleLecComments('preview', row.original.lecComments)
            }}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'dateOfVisit',
      header: 'Date of the last visit',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.dateOfVisit ? (
          <Badge
            text={DateFormatter.DDMMYYYY(row.original.dateOfVisit)}
            variant={HostFamilyProperties.dateOfVisitColor(row.original)}
          />
        ) : (
          '-'
        )
    }
  ]

  const emails = selectedData?.map((hostFamily) => hostFamily.parent1Email) ?? []

  return (
    <div>
      <UserNavBar title={'Host families'} icon={FamilyIcon} />
      <div className={'h-4'} />
      <DynamicDataTable<HostFamily, HostFamilyFilters>
        ref={tableRef}
        legends={[
          {
            icon: <RoundedIcon variant="success" icon={UserCheckIcon} />,
            label: 'Active'
          },
          {
            icon: <RoundedIcon variant="danger" icon={UserCanceledIcon} />,
            label: 'Inactive'
          }
        ]}
        onLoadingChange={setIsTableLoading}
        title={'All families'}
        deleteMessage={{
          attributeName: 'familyName',
          name: 'host family',
          deleteAll: 'Are you sure you want to delete all host families?',
          deleteMany: (data) =>
            `Are you sure you want to delete the host families "${data.map(({ familyName }) => familyName).join('", "')}"?`
        }}
        columns={columns}
        selectable
        allowSearchParam
        onRowClick={(hostFamily, event) =>
          navigateWithEvent(
            Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_UPDATE_PAGE.replace(
              ':id',
              hostFamily.id.toString()
            ),
            event
          )
        }
        endpoint={API.hostFamilies.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(filter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Add a family',
            fn: () => navigate(Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            variant: 'ghost',
            label: 'Send an email',
            fn: (hostFamilys) => toggleModal('send-email', hostFamilys)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            variant: 'ghost',
            label: 'Export CSV',
            fn: (hostFamilys) => {
              const ids = hostFamilys.map(({ id }) => id)
              downloadDocuments({ ids, endpoint: API.hostFamilies.exportHostFamiliesExcel.url })
            }
          }
        ]}
      />

      {selectedLecComments && (
        <Modal
          isOpen={lecCommentsModalState.preview}
          title={'LEC comments'}
          icon={MessageIcon}
          hideActions
          onCancel={() => closeLecCommentsModal('preview')}
        >
          <LogSection
            className={MODAL_BODY_CLASSNAME}
            data={selectedLecComments.map((comment) => ({
              comment: comment.comment,
              updatedAt: new Date(comment.updatedAt)
            }))}
            cancelText="Close"
            handleReset={() => closeLecCommentsModal('preview')}
            dateFormatter={(date) => DateFormatter.ddMMyyyyHHmm(date)}
          />
        </Modal>
      )}

      {selectedData && (
        <Modal
          isOpen={modalState['send-email']}
          title={'Send an email'}
          icon={EmailIcon}
          size={'medium'}
          hideActions
          onCancel={() => closeModel('send-email')}
        >
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            // resetText={'Cancel'}
            defaultValues={{
              emails,
              subject: '',
              body: ''
            }}
            labels={selectedData.map(
              (hostFamily) =>
                `${hostFamily.parent1FirstName} ${hostFamily.parent1LastName} - ${hostFamily.parent1Email}`
            )}
            buttonsConfig={modalButtonConfig({
              cancelText: 'Cancel',
              isEnglish: true,
              isLoading: isPending,
              onCancel: () => closeModel('send-email'),
              onConfirm: (values) => sendEmailToHostFamiliesMutation({ ...values, emails })
            })}
          />
        </Modal>
      )}

      {/* Filters Modal */}
      <FilterDrawer<HostFamilyFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <HostFamilyFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default OlPersonalSPaceHostFamiliesListPage
